import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  TextField
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FaRegCalendar } from "react-icons/fa";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import { getBonusReports } from '../utils/bonusTracker';

const Report = () => {
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [reportType, setReportType] = useState('Daily');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userBalance, setUserBalance] = useState(0);
  const [bonusData, setBonusData] = useState([]);
  const [showBonusSection, setShowBonusSection] = useState(false);

  const columns = [
    { id: 'name', label: 'Name:', width: '18%' },
    { id: 'city', label: 'City:', width: '12%' },
    { id: 'address', label: 'Address:', width: '18%' },
    // { id: 'phoneNo', label: 'Phone No:', width: '15%' },
    { id: 'income', label: 'Income', width: '10%' },
    { id: 'percent', label: 'Percent', width: '8%' },
    { id: 'totalCommission', label: 'Total Commission', width: '12%' },
    { id: 'bonusEarnings', label: 'Bonus Earnings', width: '12%' },
  ];

  // Format number as currency
  const formatNumber = (number) => {
    return parseFloat(number || 0).toFixed(2);
  };

  const fetchBonusData = async () => {
    try {
      const startDate = new Date(fromDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(toDate);
      endDate.setHours(23, 59, 59, 999);

      const bonusReports = await getBonusReports({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      
      setBonusData(bonusReports.reports || []);
      console.log('🎁 BONUS DEBUG: Fetched bonus data:', bonusReports);
    } catch (error) {
      console.error('Error fetching bonus data:', error);
      setBonusData([]);
    }
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      
      // Format dates with timezone consideration
      const startDate = new Date(fromDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(toDate);
      endDate.setHours(23, 59, 59, 999);

      // Fetch both sales and bonus data
      const [salesResponse] = await Promise.all([
        fetch('/api/reports/sales', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            fromDate: startDate.toISOString(),
            toDate: endDate.toISOString(),
            reportType: reportType.toLowerCase(),
            branchId: user.role === 'agent' ? user.branchId : undefined
          })
        }),
        fetchBonusData()
      ]);

      if (!salesResponse.ok) {
        throw new Error('Failed to fetch report data');
      }

      const data = await salesResponse.json();
      
      // Aggregate individual reports by user for display
      const aggregatedData = {};
      
      data.forEach(report => {
        const key = report.name;
        if (!aggregatedData[key]) {
          aggregatedData[key] = {
            ...report,
            income: 0,
            totalCommission: 0,
            bonusEarnings: 0
          };
        }
        aggregatedData[key].income += parseFloat(report.income) || 0;
        aggregatedData[key].totalCommission += parseFloat(report.totalCommission) || 0;
      });
      
      // Add bonus earnings to aggregated data
      if (bonusData && bonusData.length > 0) {
        bonusData.forEach(bonus => {
          const userName = bonus.User?.name || bonus.userName;
          if (userName && aggregatedData[userName]) {
            aggregatedData[userName].bonusEarnings += parseFloat(bonus.bonusAmount) || 0;
          }
        });
      }
      
      const aggregatedArray = Object.values(aggregatedData);
      console.log('🔍 REPORT DEBUG: Aggregated sales data:', aggregatedArray);
      console.log('🎁 BONUS DEBUG: Bonus data used:', bonusData);
      
      setReportData(aggregatedArray);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFromDateChange = (newValue) => {
    if (newValue && !isNaN(new Date(newValue).getTime())) {
      const date = new Date(newValue);
      date.setHours(0, 0, 0, 0);
      setFromDate(date);
    }
  };

  const handleToDateChange = (newValue) => {
    if (newValue && !isNaN(new Date(newValue).getTime())) {
      const date = new Date(newValue);
      date.setHours(23, 59, 59, 999);
      setToDate(date);
    }
  };

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const response = await fetch('/api/users/balance', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (!response.ok) throw new Error('Failed to fetch balance');
        const data = await response.json();
        setUserBalance(data.credits || 0); // Changed from data.balance to data.credits
      } catch (error) {
        console.error('Error fetching balance:', error);
        setUserBalance(0); // Set to 0 on error
      }
    };
    fetchBalance();
  }, []); // Run once on mount

  const handleSearch = () => {
    fetchReportData();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#2D2D2D', minHeight: '100vh' }}>
      {/* Header with logo and back button */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        p: 2,
        bgcolor: 'white',
        color: '#333',
        boxShadow: 1,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2,
        height: 100
      }}>
        <Typography 
          onClick={() => navigate('/')}
          sx={{ 
            color: '#333',
            fontSize: '1.25rem',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline'
            },
            mr: 2
          }}
        >
          Play Bingo
        </Typography>
        <img 
          src="/logob.png" 
          alt="Bingo Logo"
          style={{ height: '100px', width: '600px' }}
        />
      </Box>

      {/* Content wrapper */}
      <Box sx={{ display: 'flex', mt: '100px' }}> {/* Match header height */}
        <Sidebar />

        {/* Main content */}
        <Box sx={{ flexGrow: 1, marginLeft: '240px', p: 4 }}>
          <Paper sx={{ p: 3, bgcolor: 'white' }}>
            {/* Search filters */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              mb: 3,
              alignItems: 'center'
            }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={fromDate}
                  label="From"
                  onChange={handleFromDateChange}
                  slots={{
                    OpenPickerButton: CalendarTodayIcon
                  }}
                  sx={{ 
                    '& .MuiInputBase-root': {
                      bgcolor: 'white'
                    },
                    '& .MuiInputBase-input': {
                      color: 'black'
                    },
                    '& .MuiInputLabel-root': {
                      color: 'black'
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'black'
                    }
                  }}
                />
                <DatePicker
                  value={toDate}
                  label="To"
                  onChange={handleToDateChange}
                  slots={{
                    OpenPickerButton: CalendarTodayIcon
                  }}
                  sx={{ 
                    '& .MuiInputBase-root': {
                      bgcolor: 'white'
                    },
                    '& .MuiInputBase-input': {
                      color: 'black'
                    },
                    '& .MuiInputLabel-root': {
                      color: 'black'
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'black'
                    }
                  }}
                />
              </LocalizationProvider>
              
              <Select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                size="small"
                IconComponent={KeyboardArrowDownIcon}
                sx={{ 
                  minWidth: 120,
                  bgcolor: 'white',
                  '& .MuiSelect-select': {
                    color: '#333'
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#333'
                  }
                }}
              >
                <MenuItem value="Daily">Daily</MenuItem>
                <MenuItem value="Weekly">Weekly</MenuItem>
                <MenuItem value="Monthly">Monthly</MenuItem>
                <MenuItem value="Yearly">Yearly</MenuItem>
              </Select>

              <Button 
                variant="contained" 
                onClick={handleSearch}
                disabled={loading}
                sx={{
                  bgcolor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#115293'
                  }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Search'}
              </Button>
            </Box>

            {/* Results table */}
            <TableContainer component={Paper} sx={{ bgcolor: 'white' }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#1976d2' }}>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          width: column.width
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                    <TableRow 
                      key={index}
                      sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}
                    >
                      <TableCell sx={{ color: 'black' }}>{row.name}</TableCell>
                      <TableCell sx={{ color: 'black' }}>yabello</TableCell>
                      <TableCell sx={{ color: 'black' }}>{row.address}</TableCell>
                      <TableCell sx={{ color: 'black' }}>{formatNumber(row.income)}</TableCell>
                      <TableCell sx={{ color: 'black' }}>{row.percent}%</TableCell>
                      <TableCell sx={{ color: 'black' }}>{formatNumber(row.totalCommission)}</TableCell>
                      <TableCell sx={{ color: 'green', fontWeight: 'bold' }}>{formatNumber(row.bonusEarnings || 0)}</TableCell>
                    </TableRow>
                  ))}
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <Typography sx={{ color: 'green',fontSize: 24,fontWeight: 'bold' }}>Balance: {userBalance?.toLocaleString() || '0'} </Typography>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={reportData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  bgcolor: 'white',
                  '.MuiTablePagination-select': {
                    color: 'black'
                  },
                  '.MuiTablePagination-displayedRows': {
                    color: 'black'
                  },
                  '.MuiTablePagination-selectLabel': {
                    color: 'black'
                  },
                  '.MuiTablePagination-menuItem': {
                    color: 'black'
                  }
                }}
              />
            </TableContainer>
            
            {/* Bonus Summary Section */}
            {bonusData && bonusData.length > 0 && (
              <Paper sx={{ mt: 2, p: 2, bgcolor: 'white' }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontWeight: 'bold' }}>
                  🎁 Bonus Summary
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                  <Box sx={{ p: 2, bgcolor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                      Total Bonuses Awarded
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#1b5e20', fontWeight: 'bold' }}>
                      {bonusData.length}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                    <Typography variant="body2" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                      Total Bonus Amount
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                      {bonusData.reduce((sum, bonus) => sum + (parseFloat(bonus.bonusAmount) || 0), 0).toLocaleString()} ETB
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                    <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                      Bonus Type
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#0d47a1', fontWeight: 'bold' }}>
                      {[...new Set(bonusData.map(bonus => bonus.bonusType))].join(', ')}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Recent Bonuses */}
                <Typography variant="h6" sx={{ mt: 3, mb: 1, color: '#1976d2', fontWeight: 'bold' }}>
                  Recent Bonuses
                </Typography>
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {bonusData.slice(0, 10).map((bonus, index) => (
                    <Box key={index} sx={{ 
                      p: 1.5, 
                      mb: 1, 
                      bgcolor: '#f5f5f5', 
                      borderRadius: 1,
                      border: '1px solid #e0e0e0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333' }}>
                          {bonus.user?.username || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {new Date(bonus.createdAt).toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                        {parseFloat(bonus.bonusAmount || 0).toLocaleString()} ETB
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default Report;
